import { isDefined, isEmpty, isNotDefined } from '@sgme/fp';
import { Dropdown, HStack, Spacer } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import deepEqual from 'fast-deep-equal';
import { type ReactNode, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { v4 as uuid } from 'uuid';

import { selectCurrenciesQueryResult } from '@/api/config/selectors.ts';
import { useCheckCashByTradeCapture } from '@/App/tiles/CashTile/CashContext.tsx';
import { BACKGROUND_LIGHT_COLOR } from '@/App/tiles/CashTile/constants.ts';
import { NDFCheckbox } from '@/App/tiles/CashTile/shared/NDFCheckbox.tsx';
import ToggleProduct from '@/App/tiles/ToggleProduct.tsx';
import { ChangeClientDropDownItem } from '@/components/ClientSelector/ChangeClientDropDownItem.tsx';
import { TileOverridenClientIndicator } from '@/components/ClientSelector/TileOverridenClientIndicator.tsx';
import CurrencyPairPicker from '@/components/ui/CurrencyPairPicker/index.tsx';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import { cashClientIdUpdated } from '@/store/cash/slice.ts';
import { resetCash } from '@/store/cash/thunks/resetCash.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { duplicateTile, removeTile } from '@/store/tiles/slice.ts';

export type CashTileHeaderProps = {
  tileId: string;
  /** Optional slot to render between ToggleProduct and CurrencyPairPicker (e.g., predeal checks) */
  beforeCurrencyPairPicker?: ReactNode;
};

/**
 * CashTileHeader - Unified header component for all Cash tiles (Spot/Forward/NDF)
 * Handles currency pair selection, client management, and tile actions
 *
 * @example
 * // For Spot (no predeal checks)
 * <CashTileHeader tileId={tileId} />
 *
 * @example
 * // For Forward (with predeal checks)
 * <CashTileHeader
 *   tileId={tileId}
 *   beforeCurrencyPairPicker={<PredealChecksIndicator />}
 * />
 */
export function CashTileHeader(props: CashTileHeaderProps) {
  const { tileId, beforeCurrencyPairPicker } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();
  const dropdownToggleRef = useRef<HTMLButtonElement>(null);
  const dispatch = useAppDispatch();

  const {
    productType,
    currencyPair,
    notional,
    notionalCurrencyIndex,
    maturityDate,
    maturityDateTenor,
    dataByCurrencyPair,
    clientId,
  } = useAppSelector((state) => {
    const tile = selectCashTile(state, tileId);
    const { data: currencyPairData } = selectCurrenciesQueryResult(state);

    if (isNotDefined(tile)) {
      return {};
    }

    return {
      productType: tile.type,
      currencyPair: tile.properties.currencyPair,
      notional: tile.properties.notional,
      notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
      maturityDate: tile.properties.maturityDate,
      maturityDateTenor: tile.properties.maturityDateTenor,
      dataByCurrencyPair: currencyPairData?.dict,
      tradeCapture: tile.tradeCapture,
      clientId: tile.clientId,
    };
  }, deepEqual);

  const checkCashByTradeCapture = useCheckCashByTradeCapture();

  const handleCurrencyPairChange = useCallback(
    (value: string) => {
      if (value === '') {
        // don't do anything if currency pair is cleared
        return;
      }

      sgwtWebAnalytics?.trackEvent('Cash', 'Currency pair changed', value);

      // For Spot, check if currency pair is inverted to determine product type
      if (productType === 'FxSpot') {
        const currencyPairData = dataByCurrencyPair?.[value];
        const isCurrencyPairInverted = currencyPairData?.isInverted ?? false;

        if (
          isCurrencyPairInverted ||
          (isDefined(maturityDateTenor) && maturityDateTenor !== 'SP') ||
          isDefined(maturityDate)
        ) {
          checkCashByTradeCapture({
            currencyPair: value,
            isSpotWithInvertedCurrencyPair: isCurrencyPairInverted,
            notional,
            notionalCurrencyIndex,
            maturityDate,
            maturityDateTenor,
          });
        } else {
          checkCashByTradeCapture({
            currencyPair: value,
            notional,
            notionalCurrencyIndex,
            maturityDateTenor: 'SP',
          });
        }
      } else {
        // For Forward/NDF
        checkCashByTradeCapture({
          currencyPair: value,
          notional,
          notionalCurrencyIndex,
        });
      }
    },
    [
      productType,
      dataByCurrencyPair,
      notional,
      notionalCurrencyIndex,
      maturityDate,
      maturityDateTenor,
      checkCashByTradeCapture,
      sgwtWebAnalytics,
    ],
  );

  const handleDuplicate = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('Cash', 'Tile duplicated', tileId);

    dispatch(
      duplicateTile({
        sourceTileId: tileId,
        newTileId: uuid(),
        sessionId: uuid(),
      }),
    );
  }, [tileId, sgwtWebAnalytics]);

  const handleReset = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('Cash', 'Tile reseted', tileId);
    dispatch(resetCash(tileId));
  }, [tileId, sgwtWebAnalytics]);

  const handleClose = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('Cash', 'Tile closed', tileId);
    dispatch(removeTile(tileId));
  }, [tileId, sgwtWebAnalytics]);

  const handleSelectClient = useCallback(
    (clientId: number) => {
      sgwtWebAnalytics?.trackEvent('Cash', 'Tile client selected', String(clientId));
      dispatch(cashClientIdUpdated({ tileId, clientId, isTabClientOverriden: true }));
    },
    [tileId, sgwtWebAnalytics],
  );

  const handleClearSelectedClient = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('Cash', 'Tile client reseted', tileId);
    dispatch(cashClientIdUpdated({ tileId, clientId: undefined, isTabClientOverriden: true }));
  }, [tileId, sgwtWebAnalytics]);

  return (
    <div>
      <TileOverridenClientIndicator clientId={clientId} onRemoveOverride={handleClearSelectedClient} />

      <HStack className="mb-2">
        <ToggleProduct tileId={tileId} />

        {beforeCurrencyPairPicker}

        <CurrencyPairPicker
          tradeType="Cash"
          value={currencyPair}
          onChange={handleCurrencyPairChange}
          data-e2e="currency-pair-input"
        />

        <NDFCheckbox tileId={tileId} />

        <Spacer />

        <Dropdown color={BACKGROUND_LIGHT_COLOR} direction="auto" size="md" data-e2e="tile-menu">
          <Dropdown.Toggle className="btn-icon" data-e2e="menu-button">
            <i className="icon" ref={dropdownToggleRef} data-e2e="tileMenu-btn">
              more_vert
            </i>
          </Dropdown.Toggle>

          <Dropdown.Menu>
            <ChangeClientDropDownItem triggerRef={dropdownToggleRef} onSelectClient={handleSelectClient} />

            <Dropdown.Item
              onClick={handleDuplicate}
              state={isEmpty(currencyPair) ? 'disabled' : undefined}
              data-e2e="duplicate-tile-menu"
            >
              <FormattedMessage id="CashTile.SpotHeader.duplicate" />
            </Dropdown.Item>

            <Dropdown.Item
              onClick={handleReset}
              state={isEmpty(currencyPair) ? 'disabled' : undefined}
              data-e2e="reset-tile-menu"
            >
              <FormattedMessage id="CashTile.SpotHeader.reset" />
            </Dropdown.Item>

            <Dropdown.Item onClick={handleClose} data-e2e="close-tile-menu">
              <FormattedMessage id="CashTile.SpotHeader.close" />
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </HStack>
    </div>
  );
}
